import { PDF_PAGE_CACHE_SIZE } from "../config/gridLinesConfig";
import {
  loadPdfDocument,
  clearHighlights,
  highlightWordsOnPdf,
  renderPdfPage,
} from "../utils/pdfRenderingUtils";
import { calculateFitToPageScale } from "../utils/pdfScalingUtils";
import {
  throttle,
  SimpleCache,
  createCacheKey,
} from "../utils/performanceUtils";
import { useCallback, useRef, useMemo, useState } from "react";

/**
 * Hook for PDF rendering operations - coordinates PDF utilities with state management
 * Provides higher-level interface for PDF operations
 */
const usePdfRendering = (
  pdfState,
  tableState,
  refs,
  setIsInitialPdfRenderComplete
) => {
  const {
    pdfDoc,
    setPdfDoc,
    totalPages,
    setTotalPages,
    currentPage,
    setCurrentPage,
    highlights,
    setHighlights,
    pdfScale,
    setPdfScale,
    selectedWordIndex,
    setSelectedWordIndex,
  } = pdfState;

  const { canvasRef, highlightContainerRef, pdfPageDimensions } = refs;
  const initialRenderComplete = useRef(false);

  // Performance optimizations - Dynamic cache size based on document
  const pageCache = useRef(new SimpleCache(PDF_PAGE_CACHE_SIZE)); // Cache configurable number of pages
  const lastRenderParams = useRef(null);

  // Pre-rendering state
  const [preRenderingProgress, setPreRenderingProgress] = useState({
    completed: 0,
    total: 0,
    isComplete: false,
  });
  const preRenderingRef = useRef({ isRunning: false, cancelled: false });

  // Load PDF document from blob with error handling
  const loadPdf = useCallback(
    async (pdfBlob) => {
      try {
        // Clear cache when loading new PDF
        pageCache.current.clear();
        lastRenderParams.current = null;

        // Cancel any ongoing pre-rendering
        preRenderingRef.current.cancelled = true;
        setPreRenderingProgress({ completed: 0, total: 0, isComplete: false });

        await loadPdfDocument(pdfBlob, setPdfDoc, setTotalPages);
      } catch (error) {
        throw error;
      }
    },
    [setPdfDoc, setTotalPages]
  );

  // Clear all highlights from PDF
  const clearPdfHighlights = useCallback(() => {
    clearHighlights(setHighlights);
  }, [setHighlights]);

  // Highlight words on PDF with coordinate transformations
  const highlightWords = useCallback(
    (cellInfo, specificWordIndex = null) => {
      if (!cellInfo) {
        return;
      }

      highlightWordsOnPdf(
        cellInfo,
        currentPage,
        canvasRef,
        highlightContainerRef,
        pdfPageDimensions,
        setHighlights,
        specificWordIndex
      );
    },
    [
      currentPage,
      canvasRef,
      highlightContainerRef,
      pdfPageDimensions,
      setHighlights,
    ]
  );

  // Optimized render function with caching and throttling
  const renderPageInternal = useCallback(
    async (pageNumber = null, selectedCell = null, forceRender = false) => {
      const targetPage = pageNumber || currentPage;

      if (!pdfDoc || !canvasRef.current || !pdfPageDimensions.current) {
        return;
      }

      // Create cache key for this render operation
      const cacheKey = createCacheKey(
        targetPage,
        pdfScale,
        selectedCell?.row,
        selectedCell?.col,
        selectedWordIndex
      );

      // Check if we need to re-render (avoid unnecessary renders)
      if (!forceRender && lastRenderParams.current === cacheKey) {
        return; // Same parameters, no need to re-render
      }

      try {
        // Check if we have a cached version for this page and scale
        const pageCacheKey = createCacheKey(targetPage, pdfScale);
        let cachedImageData = pageCache.current.get(pageCacheKey);

        if (!cachedImageData || forceRender) {
          // Render the page to get image data
          await renderPdfPage(
            pdfDoc,
            canvasRef,
            targetPage,
            pdfScale,
            pdfPageDimensions,
            null, // Don't apply highlights during caching
            null,
            null
          );

          // Cache the rendered page
          const canvas = canvasRef.current;
          if (canvas) {
            cachedImageData = canvas
              .getContext("2d")
              .getImageData(0, 0, canvas.width, canvas.height);
            pageCache.current.set(pageCacheKey, cachedImageData);
          }
        } else {
          // Use cached version
          const canvas = canvasRef.current;
          if (canvas) {
            const context = canvas.getContext("2d");
            context.putImageData(cachedImageData, 0, 0);
          }
        }

        // Apply highlights after rendering/restoring from cache
        if (selectedCell && highlightWords) {
          highlightWords(selectedCell, selectedWordIndex);
        }

        lastRenderParams.current = cacheKey;
      } catch (error) {
        throw error;
      }

      // Set initial render complete flag only once
      if (!initialRenderComplete.current) {
        initialRenderComplete.current = true;
        setIsInitialPdfRenderComplete(true);

        // Auto fit-to-page on initial load
        setTimeout(() => {
          if (canvasRef.current && pdfPageDimensions.current) {
            const optimalScale = calculateFitToPageScale(
              canvasRef.current,
              pdfPageDimensions.current
            );

            if (optimalScale !== null) {
              setPdfScale(optimalScale);
            }
          }
        }, 100); // Small delay to ensure canvas is fully rendered
      }
    },
    [
      pdfDoc,
      currentPage,
      pdfScale,
      selectedWordIndex,
      highlightWords,
      canvasRef,
      pdfPageDimensions,
      setPdfScale,
      setIsInitialPdfRenderComplete,
    ]
  );

  // Throttled version for frequent calls (like scale changes)
  const renderPageThrottled = useMemo(
    () => throttle(renderPageInternal, 16), // ~60fps throttling
    [renderPageInternal]
  );

  // Public render function
  const renderPage = useCallback(
    async (pageNumber = null, selectedCell = null, forceRender = false) => {
      if (forceRender) {
        // Force immediate render (page changes, etc.)
        await renderPageInternal(pageNumber, selectedCell, true);
      } else {
        // Use throttled version for performance
        renderPageThrottled(pageNumber, selectedCell, false);
      }
    },
    [renderPageInternal, renderPageThrottled]
  );

  // Navigate to specific page
  const goToPage = useCallback(
    (pageNumber) => {
      const targetPage = Math.max(1, Math.min(pageNumber, totalPages));
      if (targetPage !== currentPage) {
        setCurrentPage(targetPage);
      }
    },
    [currentPage, totalPages, setCurrentPage]
  );

  // Navigate to next page
  const nextPage = useCallback(() => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  }, [currentPage, totalPages, goToPage]);

  // Navigate to previous page
  const previousPage = useCallback(() => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, goToPage]);

  // Check if navigation is available
  const canNavigateNext = currentPage < totalPages;
  const canNavigatePrevious = currentPage > 1;

  // Get rendering status
  const getRenderingStatus = useCallback(() => {
    return {
      isLoaded: !!pdfDoc,
      currentPage,
      totalPages,
      scale: pdfScale,
      highlightCount: highlights?.length || 0,
      canNavigateNext,
      canNavigatePrevious,
    };
  }, [
    pdfDoc,
    currentPage,
    totalPages,
    pdfScale,
    highlights,
    canNavigateNext,
    canNavigatePrevious,
  ]);

  // Clear rendering cache (useful for memory management)
  const clearCache = useCallback(() => {
    pageCache.current.clear();
    lastRenderParams.current = null;
  }, []);

  // Pre-render all pages in background for instant page flipping
  const preRenderAllPages = useCallback(
    async (startFromPage = 1) => {
      if (
        !pdfDoc ||
        !canvasRef.current ||
        !pdfPageDimensions.current ||
        totalPages === 0
      ) {
        return;
      }

      // Don't start if already running
      if (preRenderingRef.current.isRunning) {
        return;
      }

      preRenderingRef.current.isRunning = true;
      preRenderingRef.current.cancelled = false;

      setPreRenderingProgress({
        completed: 0,
        total: totalPages,
        isComplete: false,
      });

      // Create priority list: current page first, then nearby pages, then all others
      const priorityPages = [];

      // Add current page first
      priorityPages.push(startFromPage);

      // Add nearby pages (±3 pages from current)
      for (let distance = 1; distance <= 3; distance++) {
        if (startFromPage - distance >= 1)
          priorityPages.push(startFromPage - distance);
        if (startFromPage + distance <= totalPages)
          priorityPages.push(startFromPage + distance);
      }

      // Add all remaining pages
      for (let page = 1; page <= totalPages; page++) {
        if (!priorityPages.includes(page)) {
          priorityPages.push(page);
        }
      }

      let completed = 0;

      const renderNextPage = async () => {
        if (preRenderingRef.current.cancelled || completed >= totalPages) {
          if (!preRenderingRef.current.cancelled) {
            setPreRenderingProgress({
              completed: totalPages,
              total: totalPages,
              isComplete: true,
            });
          }
          preRenderingRef.current.isRunning = false;
          return;
        }

        const pageToRender = priorityPages[completed];
        if (!pageToRender) {
          preRenderingRef.current.isRunning = false;
          return;
        }

        try {
          const pageCacheKey = createCacheKey(pageToRender, pdfScale);

          // Skip if already cached
          if (!pageCache.current.get(pageCacheKey)) {
            // Create a temporary canvas for background rendering
            const tempCanvas = document.createElement("canvas");
            const tempCanvasRef = { current: tempCanvas };

            await renderPdfPage(
              pdfDoc,
              tempCanvasRef,
              pageToRender,
              pdfScale,
              pdfPageDimensions,
              null, // No highlights for background rendering
              null,
              null
            );

            // Cache the rendered page
            if (tempCanvas.width > 0 && tempCanvas.height > 0) {
              const context = tempCanvas.getContext("2d");
              const imageData = context.getImageData(
                0,
                0,
                tempCanvas.width,
                tempCanvas.height
              );
              pageCache.current.set(pageCacheKey, imageData);
            }
          }

          completed++;
          setPreRenderingProgress({
            completed,
            total: totalPages,
            isComplete: false,
          });

          // Use requestIdleCallback for non-blocking execution
          if (window.requestIdleCallback) {
            window.requestIdleCallback(renderNextPage, { timeout: 100 });
          } else {
            // Fallback for browsers without requestIdleCallback
            setTimeout(renderNextPage, 10);
          }
        } catch (error) {
          completed++;
          setPreRenderingProgress({
            completed,
            total: totalPages,
            isComplete: false,
          });

          // Continue with next page
          if (window.requestIdleCallback) {
            window.requestIdleCallback(renderNextPage, { timeout: 100 });
          } else {
            setTimeout(renderNextPage, 10);
          }
        }
      };

      // Start the pre-rendering process
      renderNextPage();
    },
    [pdfDoc, canvasRef, pdfPageDimensions, totalPages, pdfScale]
  );

  // Cancel pre-rendering
  const cancelPreRendering = useCallback(() => {
    preRenderingRef.current.cancelled = true;
    preRenderingRef.current.isRunning = false;
    setPreRenderingProgress((prev) => ({ ...prev, isComplete: false }));
  }, []);

  // Batch operation: load PDF and render first page
  const loadAndRender = useCallback(
    async (pdfBlob, initialPage = 1) => {
      await loadPdf(pdfBlob);
      if (initialPage !== currentPage) {
        setCurrentPage(initialPage);
      }
      // Note: renderPage will be called by useEffect when currentPage changes
    },
    [loadPdf, currentPage, setCurrentPage]
  );

  return {
    // Core operations
    loadPdf,
    renderPage,
    loadAndRender,
    clearCache,

    // Pre-rendering for instant page flips
    preRenderAllPages,
    cancelPreRendering,
    preRenderingProgress,

    // Highlighting
    highlightWords,
    clearPdfHighlights,

    // Navigation
    goToPage,
    nextPage,
    previousPage,

    // State accessors
    pdfDoc,
    currentPage,
    totalPages,
    pdfScale,
    highlights,
    selectedWordIndex,
    setSelectedWordIndex,

    // Status
    getRenderingStatus,
    canNavigateNext,
    canNavigatePrevious,
  };
};

export default usePdfRendering;
